import React, { useId } from 'react';
import { cx } from './cx';

const control =
  'w-full rounded-lg border bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-chip';

/** The accessibility props `Field` hands to its control. */
export interface ControlProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
  'aria-required': true | undefined;
  className: string;
}

interface FieldShape {
  label: string;
  hint?: React.ReactNode;
  error?: string;
  required?: boolean;
  className?: string;
}

/**
 * Label + control + hint + error wired together for screen readers
 * (htmlFor, aria-describedby, aria-invalid). `children` renders the control:
 * <Field label="Age">{(p) => <input {...p} />}</Field>, or use TextField / SelectField below.
 */
export function Field({ label, hint, error, required, children, className }: FieldShape & { children: (props: ControlProps) => React.ReactNode }) {
  const id = useId();
  const describedBy = [hint && !error && `${id}-hint`, error && `${id}-err`].filter(Boolean).join(' ') || undefined;
  const props: ControlProps = {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
    'aria-required': required || undefined,
    className: cx(control, error ? 'border-danger' : 'border-line'),
  };
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold text-muted">
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      {children(props)}
      {hint && !error && <p id={`${id}-hint`} className="mt-1 text-xs text-muted">{hint}</p>}
      {error && <p id={`${id}-err`} className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}

type TextFieldProps = FieldShape & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className' | 'required'>;

export const TextField = ({ label, hint, error, required, className, ...input }: TextFieldProps) => (
  <Field label={label} hint={hint} error={error} required={required} className={className}>
    {(p) => <input {...p} {...input} />}
  </Field>
);

type SelectFieldProps = FieldShape & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'required'>;

export const SelectField = ({ label, hint, error, required, className, children, ...select }: SelectFieldProps) => (
  <Field label={label} hint={hint} error={error} required={required} className={className}>
    {(p) => <select {...p} {...select}>{children}</select>}
  </Field>
);

interface ToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/** Switch row. `description` is optional helper text under the label (linked for screen readers). */
export function Toggle({ label, description, checked, onChange, disabled }: ToggleProps) {
  const descId = useId();
  return (
    <label className={cx('flex cursor-pointer items-start justify-between gap-3 rounded-lg border px-3.5 py-3 text-sm transition-colors', checked ? 'border-brand-300 bg-brand-50/50' : 'border-line hover:bg-canvas', disabled && 'opacity-60')}>
      <span>
        <span className="block font-medium text-ink">{label}</span>
        {description && <span id={descId} className="mt-0.5 block text-xs text-muted">{description}</span>}
      </span>
      <input
        type="checkbox"
        role="switch"
        className="mt-0.5 h-4 w-4 flex-none accent-brand-600"
        aria-describedby={description ? descId : undefined}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}
